import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderKanban,
  Microscope,
  Wallet,
  Users,
  Package,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { NAV_SECTIONS, findActiveNav } from './navConfig';
import { can } from '../auth/store';
import './Sidebar.css';

// Icons live here (not in navConfig) so the config stays React/lucide-free
// and unit-testable. Keyed by section.key.
const SECTION_ICON: Record<string, LucideIcon> = {
  overview: LayoutDashboard,
  projects: FolderKanban,
  casework: Microscope,
  financials: Wallet,
  directory: Users,
  resources: Package,
  settings: Settings,
};

/**
 * Left vertical rail of top-level sections. Clicking a section navigates
 * to its first item; the active section is derived from the URL via
 * findActiveNav (so a section is active for ANY of its items / nested
 * routes, not just its first link). The selected section's items render
 * across the top in <SectionTabs>.
 */
export function Sidebar(): JSX.Element {
  const { pathname } = useLocation();
  const active = findActiveNav(pathname);

  // Hide permission-gated sections (e.g. Settings) from users who lack the
  // grants — they'd otherwise land on a hub with no visible tabs.
  const sections = NAV_SECTIONS.filter(
    (s) => !s.requiresAnyPermission || s.requiresAnyPermission.some((p) => can(p)),
  );

  return (
    <nav className="nav-rail" aria-label="Primary">
      <ul className="nav-cat-list">
        {sections.map((section) => {
          const Icon = SECTION_ICON[section.key];
          const isActive = active?.section.key === section.key;
          const first = section.items[0];
          if (!first) return null;
          return (
            <li key={section.key}>
              {/* className is a function so React Router doesn't also apply
                  its own end-exact active class — we control "active" via
                  findActiveNav (whole-section match), not the first link. */}
              <NavLink
                to={first.to}
                end={first.to === '/app'}
                className={() => `nav-cat${isActive ? ' active' : ''}`}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className="nav-cat-icon" aria-hidden="true">
                  {Icon ? <Icon size={18} strokeWidth={1.75} /> : null}
                </span>
                <span className="nav-cat-label">{section.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
